import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Eye, EyeOff, Save, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API } from "@/apiConfig";
import ContentPublicPreview, { announceContentSaved } from "@/components/ContentPublicPreview";
const empty = { name: "", text: "", role: "", visible: true };

export default function GestionTemoignages() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [initialForm, setInitialForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const load = () => axios.get(`${API}/admin/testimonials`).then(r => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => toast.error("Erreur"));

  useEffect(() => { load(); }, []);

  const openNew = () => { const next={...empty};setForm(next);setInitialForm(next);setEditing("new"); };
  const openEdit = t => { const next={ name: t.name, text: t.text, role: t.role || "", visible: t.visible };setForm(next);setInitialForm(next);setEditing(t.id); };
  const close = () => { setEditing(null);setForm({...empty});setInitialForm({...empty}); };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing === "new") await axios.post(`${API}/admin/testimonials`, form);
      else await axios.put(`${API}/admin/testimonials/${editing}`, form);
      announceContentSaved();
      toast.success(editing === "new" ? "Témoignage ajouté !" : "Témoignage mis à jour !");
      close(); load();
    } catch { toast.error("Erreur"); } finally { setLoading(false); }
  };

  const toggleVisible = async t => {
    await axios.put(`${API}/admin/testimonials/${t.id}`, { ...t, visible: !t.visible });
    toast.success(!t.visible ? "Témoignage affiché" : "Témoignage masqué");
    load();
  };

  const handleDelete = async id => {
    if (!window.confirm("Supprimer ce témoignage ?")) return;
    await axios.delete(`${API}/admin/testimonials/${id}`);
    toast.success("Supprimé"); load();
  };

  if (editing !== null) {
    return (
      <ContentPublicPreview url="/#temoignages" focusHash="#temoignages" title="Section témoignages" status={editing!=="new"&&form.visible?"published":"draft"} dirty={JSON.stringify(form)!==JSON.stringify(initialForm)}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="font-playfair text-3xl text-charcoal">{editing === "new" ? "Nouveau témoignage" : "Modifier"}</h1>
          <button onClick={close} className="p-2 text-charcoal-muted hover:text-charcoal"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 border border-sage/10 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Nom *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="Prénom N."
                className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none" />
            </div>
            <div>
              <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Rôle / Profession</label>
              <input value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} placeholder="Ex: Cadre, 42 ans"
                className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none" />
            </div>
          </div>
          <div>
            <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Témoignage *</label>
            <textarea value={form.text} onChange={e => setForm(f => ({ ...f, text: e.target.value }))} rows={4} required
              placeholder="Ce que la personne a dit..."
              className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none resize-none" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.visible} onChange={e => setForm(f => ({ ...f, visible: e.target.checked }))} className="accent-sage" />
            <span className="font-manrope text-sm text-charcoal">Visible sur le site</span>
          </label>
          <div className="flex gap-3">
            <button type="submit" disabled={loading} data-testid="temoignage-save-btn" className="btn-primary flex items-center gap-2 disabled:opacity-60">
              {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Save size={15} /> Enregistrer</>}
            </button>
            <button type="button" onClick={close} className="btn-secondary">Annuler</button>
          </div>
        </form>
      </div>
      </ContentPublicPreview>
    );
  }

  return (
    <ContentPublicPreview url="/#temoignages" focusHash="#temoignages" title="Section témoignages">
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-playfair text-3xl text-charcoal">Témoignages</h1>
          <p className="font-manrope text-sm text-charcoal-muted">{items.length} témoignage{items.length > 1 ? "s" : ""}</p>
        </div>
        <button onClick={openNew} data-testid="new-temoignage-btn" className="btn-primary flex items-center gap-2"><Plus size={16} /> Ajouter</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((t, i) => (
          <div key={t.id || i} data-testid={`temoignage-card-${i}`} className={`bg-white rounded-2xl p-5 border transition-all ${t.visible ? "border-sage/10" : "border-dashed border-sage/20 opacity-60"}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-manrope font-medium text-sm text-charcoal">{t.name}</p>
                {t.role && <p className="font-manrope text-xs text-charcoal-muted">{t.role}</p>}
              </div>
              <span className={`font-manrope text-xs px-2 py-0.5 rounded-full ${t.visible ? "bg-sage/10 text-sage-dark" : "bg-bone text-charcoal-muted"}`}>
                {t.visible ? "Visible" : "Masqué"}
              </span>
            </div>
            <p className="font-manrope text-sm text-charcoal-muted italic mb-4">"{t.text}"</p>
            <div className="flex gap-2">
              <button onClick={() => openEdit(t)} className="p-1.5 text-charcoal-muted hover:text-sage transition-colors"><Edit size={15} /></button>
              <button onClick={() => toggleVisible(t)} className="p-1.5 text-charcoal-muted hover:text-sage transition-colors">
                {t.visible ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
              <button onClick={() => handleDelete(t.id)} className="p-1.5 text-charcoal-muted hover:text-red-500 transition-colors"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-2 py-16 text-center font-manrope text-charcoal-muted">Aucun témoignage. Ajoutez le premier !</div>}
      </div>
    </div>
    </ContentPublicPreview>
  );
}
